import { createElement, type ElementType, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

const textVariants = cva("", {
  variants: {
    variant: {
      /*
       * `leading-display` is not redundant next to `text-display`, which
       * already carries 1.12. It pins the line height across the `sm:text-3xl`
       * step up, which would otherwise bring `--text-3xl--line-height` (1.18)
       * with it and loosen the heading on a wide screen.
       */
      display:
        "font-display text-display font-semibold leading-display text-foreground sm:text-3xl",
      title: "font-display text-title font-semibold text-foreground",
      heading:
        "font-display text-lg font-semibold tracking-tight text-foreground leading-snug",
      body: "text-base text-foreground leading-relaxed",
      muted: "text-body-sm text-foreground-muted leading-relaxed",
      subtle: "text-sm text-foreground-muted leading-relaxed",
      label: "text-label font-semibold uppercase text-foreground-muted",
      caption: "text-xs text-foreground-muted leading-snug",
    },
  },
  defaultVariants: {
    variant: "body",
  },
});

/**
 * Line length cap for running text. Uses the `measure` tokens from
 * `@samklang/design-tokens/tailwind-theme`, expressed in `ch` so the limit
 * follows the typeface rather than a pixel guess. Always a max-width, so a
 * narrow screen can never be forced wider than its column.
 */
export type TextMeasure = "narrow" | "default" | "wide";

const measureClasses: Record<TextMeasure, string> = {
  narrow: "max-w-measure-narrow",
  default: "max-w-measure",
  wide: "max-w-measure-wide",
};

const defaultElements: Record<
  NonNullable<VariantProps<typeof textVariants>["variant"]>,
  ElementType
> = {
  display: "h1",
  title: "h1",
  heading: "h2",
  body: "p",
  muted: "p",
  subtle: "p",
  label: "p",
  caption: "span",
};

export type TextProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof textVariants> & {
    as?: ElementType;
    /** Cap the line length for running text. Omit for labels and chips. */
    measure?: TextMeasure;
  };

export function Text({
  className,
  variant = "body",
  as,
  measure,
  ...props
}: TextProps) {
  const tag = as ?? defaultElements[variant ?? "body"];
  return createElement(tag, {
    className: cn(
      textVariants({ variant }),
      measure && measureClasses[measure],
      className
    ),
    ...props,
  });
}

export { textVariants };
